/**
 * Sender page — input, settings, live rateless QR playback and stats.
 */
import { QrSender, QrStreamSchedule, ensureQwertyWasm } from '@qwerty/engine/qwerty';
import type { EccLevel } from '@qwerty/engine/qr/qr_encode';
import type { ParallelQRCount } from '@qwerty/engine/sender/parallel_striping';
import { FrameRenderPool } from './render_pool';
import { ECC_LEVELS, QR_VERSIONS, eccIndex, layoutFor, profileFor } from '../profiles';
import { loadSettings, saveSettings, type AppSettings } from '../settings';

const MANIFEST_PERIOD = 24;
const TARGET_TILE_PX = 320;
const PREFETCH_FRAMES = 6;
const PARALLEL_OPTIONS: ParallelQRCount[] = [1, 2, 4, 6, 8];

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export function createSenderPage(setStatus: (text: string) => void): HTMLElement {
  const ui = window.__SKF_UI;
  const settings: AppSettings = loadSettings();
  const persist = () => saveSettings(settings);

  const field = (label: string, control: HTMLElement): HTMLElement => {
    const wrap = ui.el('div', 'qwerty-field');
    wrap.appendChild(ui.el('span', 'skf-caption-heading', label));
    wrap.appendChild(control);
    return wrap;
  };

  const makeSlider = (min: number, max: number, step: number, value: number, onInput: (v: number) => void): HTMLInputElement => {
    const input = document.createElement('input');
    input.type = 'range';
    input.className = 'skf-slider';
    input.min = String(min);
    input.max = String(max);
    input.step = String(step);
    input.value = String(value);
    input.addEventListener('input', () => onInput(Number(input.value)));
    return input;
  };

  const makeSwitch = (initial: boolean, onChange: (value: boolean) => void): HTMLElement => {
    let checked = initial;
    const el = ui.el('div', 'skf-switch' + (checked ? ' checked' : ''));
    el.setAttribute('role', 'switch');
    el.tabIndex = 0;
    el.appendChild(ui.el('div', 'skf-switch-slider'));
    const toggle = () => {
      checked = !checked;
      el.classList.toggle('checked', checked);
      onChange(checked);
    };
    el.addEventListener('click', toggle);
    el.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        toggle();
      }
    });
    return el;
  };

  const stat = (label: string): { root: HTMLElement; value: HTMLElement } => {
    const box = ui.el('div', 'qwerty-stat');
    box.appendChild(ui.el('span', 'skf-caption skf-label-dim', label));
    const value = ui.el('b', '', '—');
    box.appendChild(value);
    return { root: box, value };
  };

  const root = ui.el('div', 'qwerty-page-root skf-container skf-container--fluid');
  const row = ui.el('div', 'skf-row skf-g-3');
  root.appendChild(row);

  const settingsCol = ui.el('div', 'skf-col-12 skf-col-lg-4');
  const stageCol = ui.el('div', 'skf-col-12 skf-col-lg-8');
  row.append(settingsCol, stageCol);

  // ── settings card ───────────────────────────────────────────────────────────
  const settingsCard = ui.el('section', 'skf-card qwerty-card');
  settingsCard.appendChild(ui.el('h2', 'skf-title-4', '发送设置'));
  settingsCol.appendChild(settingsCard);

  let inputMode: 'text' | 'file' = 'text';
  const modeRow = ui.el('div', 'qwerty-seg-row');

  const textArea = document.createElement('textarea');
  textArea.className = 'skf-textarea';
  textArea.rows = 5;
  textArea.placeholder = '输入要传输的文本…';
  textArea.value = '你好，QweRty！';

  const drop = ui.el('div', 'qwerty-drop');
  const dropLabel = ui.el('span', '', '点击或拖拽文件到此处');
  drop.appendChild(dropLabel);
  const fileInput = document.createElement('input');
  fileInput.type = 'file';
  fileInput.className = 'qwerty-hidden';
  drop.appendChild(fileInput);
  let file: File | null = null;
  const refreshDrop = () => {
    dropLabel.textContent = file ? file.name + ' · ' + file.size + ' B' : '点击或拖拽文件到此处';
  };
  drop.addEventListener('click', () => fileInput.click());
  fileInput.addEventListener('change', () => {
    file = fileInput.files && fileInput.files[0] ? fileInput.files[0] : null;
    refreshDrop();
  });
  drop.addEventListener('dragover', (event) => { event.preventDefault(); drop.classList.add('is-over'); });
  drop.addEventListener('dragleave', () => drop.classList.remove('is-over'));
  drop.addEventListener('drop', (event) => {
    event.preventDefault();
    drop.classList.remove('is-over');
    file = event.dataTransfer && event.dataTransfer.files[0] ? event.dataTransfer.files[0] : null;
    refreshDrop();
  });

  const syncMode = () => {
    textArea.classList.toggle('qwerty-hidden', inputMode !== 'text');
    drop.classList.toggle('qwerty-hidden', inputMode !== 'file');
  };
  ui.segmented(modeRow, [
    { id: 'text', label: '文本' },
    { id: 'file', label: '文件' },
  ], inputMode, (id) => { inputMode = id === 'file' ? 'file' : 'text'; syncMode(); });

  settingsCard.appendChild(field('输入方式', modeRow));
  settingsCard.appendChild(textArea);
  settingsCard.appendChild(drop);
  syncMode();

  const versionDrop = ui.dropdown({
    items: QR_VERSIONS.map((v) => ({ id: String(v), label: 'V' + v })),
    value: String(settings.version),
    onChange: (id) => { settings.version = Number(id); persist(); },
  });
  settingsCard.appendChild(field('QR 版本', versionDrop.root));

  const eccRow = ui.el('div', 'qwerty-seg-row');
  ui.segmented(eccRow, ECC_LEVELS.map((e) => ({ id: e, label: e })), settings.ecc, (id) => {
    settings.ecc = id as EccLevel;
    persist();
  });
  settingsCard.appendChild(field('纠错等级', eccRow));

  const parallelDrop = ui.dropdown({
    items: PARALLEL_OPTIONS.map((n) => ({ id: String(n), label: String(n) + ' 联' })),
    value: String(settings.parallel),
    onChange: (id) => { settings.parallel = Number(id) as ParallelQRCount; persist(); },
  });
  settingsCard.appendChild(field('并行 QR', parallelDrop.root));

  const fpsValue = ui.el('span', 'qwerty-value', settings.fps + ' fps');
  const fpsSlider = makeSlider(2, 60, 1, settings.fps, (v) => {
    settings.fps = v;
    fpsValue.textContent = v + ' fps';
    persist();
  });
  const fpsRow = ui.el('div');
  fpsRow.append(fpsSlider, fpsValue);
  settingsCard.appendChild(field('播放帧率', fpsRow));

  const repairValue = ui.el('span', 'qwerty-value', settings.repairPercent + '%');
  const repairSlider = makeSlider(0, 100, 5, settings.repairPercent, (v) => {
    settings.repairPercent = v;
    repairValue.textContent = v + '%';
    persist();
  });
  const repairRow = ui.el('div');
  repairRow.append(repairSlider, repairValue);
  settingsCard.appendChild(field('初始修复率', repairRow));

  const compressSwitch = makeSwitch(settings.compress, (value) => { settings.compress = value; persist(); });
  settingsCard.appendChild(field('压缩载荷', compressSwitch));

  const actions = ui.el('div', 'qwerty-actions');
  const startBtn = document.createElement('button');
  startBtn.className = 'skf-btn skf-btn-suggested';
  startBtn.textContent = '开始传输';
  actions.appendChild(startBtn);
  const resetBtn = document.createElement('button');
  resetBtn.className = 'skf-btn skf-btn-flat';
  resetBtn.textContent = '清空';
  resetBtn.addEventListener('click', () => {
    stop();
    textArea.value = '';
    file = null;
    refreshDrop();
  });
  actions.appendChild(resetBtn);
  settingsCard.appendChild(actions);

  // ── stage card ──────────────────────────────────────────────────────────────
  const stageCard = ui.el('section', 'skf-card qwerty-card');
  const stageHead = ui.el('div', 'qwerty-status-row');
  stageHead.appendChild(ui.el('h2', 'skf-title-4', '实时播放'));
  const badge = ui.el('span', 'skf-tag', '待机');
  stageHead.appendChild(badge);
  stageCard.appendChild(stageHead);

  const stage = ui.el('div', 'qwerty-stage');
  const canvas = document.createElement('canvas');
  canvas.width = 420;
  canvas.height = 420;
  stage.appendChild(canvas);
  stageCard.appendChild(stage);

  const statsGrid = ui.el('div', 'qwerty-stats');
  const statFrames = stat('已播放帧');
  const statFps = stat('实际 FPS');
  const statSymbols = stat('源符号');
  const statSize = stat('载荷大小');
  const statTransfer = stat('传输 ID');
  const statSymbolSize = stat('符号大小');
  statsGrid.append(
    statFrames.root, statFps.root, statSymbols.root,
    statSize.root, statTransfer.root, statSymbolSize.root,
  );
  stageCard.appendChild(statsGrid);
  stageCol.appendChild(stageCard);

  // ── playback ────────────────────────────────────────────────────────────────
  let sender: QrSender | null = null;
  let schedule: QrStreamSchedule | null = null;
  let pool: FrameRenderPool | null = null;
  let running = false;
  let frameIndex = 0;
  let nextFrameAt = 0;
  let timer: number | null = null;
  let cache = new Map<number, ImageBitmap>();
  let pending = new Set<number>();
  let scale = 2;
  let columns = 2;
  let tilePx = 0;
  let startedAt = 0;

  const framePackets = (frame: number): Uint8Array[] =>
    schedule ? schedule.framePackets(frame) : [];

  const requestFrame = (frame: number): void => {
    const active = sender;
    const activePool = pool;
    if (!active || !activePool || cache.has(frame) || pending.has(frame)) return;
    pending.add(frame);
    activePool
      .render(framePackets(frame), settings.version, eccIndex(settings.ecc), scale, columns)
      .then((bitmap) => {
        cache.set(frame, bitmap);
        const cutoff = frame - 6;
        for (const entry of Array.from(cache.entries())) {
          if (entry[0] < cutoff) {
            entry[1].close();
            cache.delete(entry[0]);
          }
        }
      })
      .catch((err) => setStatus('渲染失败：' + messageOf(err)))
      .finally(() => pending.delete(frame));
  };

  const updateStats = (): void => {
    statFrames.value.textContent = String(frameIndex);
    const elapsed = (performance.now() - startedAt) / 1000;
    statFps.value.textContent = elapsed > 0 ? (frameIndex / elapsed).toFixed(1) : '—';
  };

  const tick = (): void => {
    if (!running) return;
    const bitmap = cache.get(frameIndex);
    if (bitmap) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(bitmap, 0, 0);
      }
      frameIndex++;
      updateStats();
      // Compensated cadence: schedule against a target timestamp so the work
      // done in this tick does not add to the frame period.
      const interval = Math.max(1, Math.round(1000 / settings.fps));
      const now = performance.now();
      nextFrameAt += interval;
      if (nextFrameAt < now) nextFrameAt = now + interval;
      for (let ahead = 1; ahead <= PREFETCH_FRAMES; ahead++) requestFrame(frameIndex + ahead);
      timer = window.setTimeout(tick, Math.max(0, nextFrameAt - now));
    } else {
      // Current frame is not rendered yet — poll soon instead of blocking the
      // target cadence.
      requestFrame(frameIndex);
      for (let ahead = 1; ahead <= PREFETCH_FRAMES; ahead++) requestFrame(frameIndex + ahead);
      timer = window.setTimeout(tick, 2);
    }
  };

  const stop = (): void => {
    running = false;
    if (timer !== null) {
      window.clearTimeout(timer);
      timer = null;
    }
    if (pool) {
      pool.terminate();
      pool = null;
    }
    for (const bitmap of cache.values()) bitmap.close();
    cache = new Map();
    pending = new Set();
    sender = null;
    schedule = null;
    startBtn.textContent = '开始传输';
    badge.textContent = '已停止';
    badge.className = 'skf-tag';
  };

  const start = async (): Promise<void> => {
    if (running) return;
    let data: Uint8Array;
    let isText = true;
    let filename: string | undefined;
    let mime: string | undefined;

    if (inputMode === 'text') {
      data = new TextEncoder().encode(textArea.value.length > 0 ? textArea.value : 'QweRty');
    } else {
      if (!file) {
        setStatus('请先选择文件');
        return;
      }
      data = new Uint8Array(await file.arrayBuffer());
      isText = false;
      filename = file.name;
      mime = file.type || 'application/octet-stream';
    }

    const profile = profileFor(settings.version, settings.ecc);
    const layout = layoutFor(settings.parallel);
    columns = layout.columns;
    const rows = layout.rows;
    const totalModules = settings.version * 4 + 17 + 8;
    scale = Math.max(2, Math.round(TARGET_TILE_PX / totalModules));
    tilePx = totalModules * scale;
    canvas.width = columns * tilePx;
    canvas.height = rows * tilePx;

    // RaptorQEncoder is a wasm-bindgen class: the module must be instantiated
    // before it can be constructed.
    try {
      await ensureQwertyWasm();
    } catch (err) {
      setStatus('RaptorQ WASM 初始化失败：' + messageOf(err));
      return;
    }

    try {
      sender = QrSender.create(data, {
        isText,
        compress: settings.compress,
        filename,
        mime,
        maxTransportPayloadSize: profile.maxPayloadSize,
        qrVersion: settings.version,
        eccLevel: eccIndex(settings.ecc),
        parallelCount: settings.parallel,
      });
    } catch (err) {
      setStatus('创建发送器失败：' + messageOf(err));
      return;
    }

    schedule = new QrStreamSchedule(sender, { parallelCount: settings.parallel, manifestPeriod: MANIFEST_PERIOD });
    pool = new FrameRenderPool(Math.max(1, Math.min(4, settings.parallel)));
    cache = new Map();
    pending = new Set();
    frameIndex = 0;
    nextFrameAt = performance.now();
    startedAt = performance.now();
    running = true;

    const info = sender.info;
    statSymbols.value.textContent = String(info.sourceSymbols);
    statSize.value.textContent = info.dataLength + ' B';
    statTransfer.value.textContent = '#' + info.transferId.toString(16);
    statSymbolSize.value.textContent = info.symbolSize + ' B';
    statFrames.value.textContent = '0';
    statFps.value.textContent = '—';
    badge.textContent = '传输中';
    badge.className = 'skf-tag skf-tag--success';
    startBtn.textContent = '停止传输';
    setStatus('传输中 · ' + info.sourceSymbols + ' 个源符号 · ' + columns + '×' + rows + ' 布局');
    tick();
  };

  startBtn.addEventListener('click', () => {
    if (running) stop();
    else void start();
  });

  return root;
}
